import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { useApi } from '../lib/api';
import { useAuth } from '../contexts/useAuth';
import {
    format,
    startOfWeek,
    endOfWeek,
    addWeeks,
    subWeeks,
    eachDayOfInterval,
    isToday,
    getYear,
} from 'date-fns';
import { fr } from 'date-fns/locale';
import {
    ChevronLeft,
    ChevronRight,
    Calendar,
    Send,
    Loader2,
    Clock,
    AlertTriangle,
} from 'lucide-react';
import { cn, dateDepuisUrl, hoursToDays } from '../lib/utils';
import type { ApiResponse, TimeEntry as TimeEntryType } from '../types';

// Calcul de Pâques (algorithme de Meeus/Jones/Butcher)
function getEasterDate(year: number): Date {
    const a = year % 19;
    const b = Math.floor(year / 100);
    const c = year % 100;
    const d = Math.floor(b / 4);
    const e = b % 4;
    const f = Math.floor((b + 8) / 25);
    const g = Math.floor((b - f + 1) / 3);
    const h = (19 * a + b - d - g + 15) % 30;
    const i = Math.floor(c / 4);
    const k = c % 4;
    const l = (32 + 2 * e + 2 * i - h - k) % 7;
    const m = Math.floor((a + 11 * h + 22 * l) / 451);
    const month = Math.floor((h + l - 7 * m + 114) / 31);
    const day = ((h + l - 7 * m + 114) % 31) + 1;
    return new Date(year, month - 1, day);
}

// Jours fériés français
function getFrenchHolidays(year: number): Map<string, string> {
    const holidays = new Map<string, string>();

    // Jours fixes
    holidays.set(`${year}-01-01`, 'Jour de l\'An');
    holidays.set(`${year}-05-01`, 'Fête du Travail');
    holidays.set(`${year}-05-08`, 'Victoire 1945');
    holidays.set(`${year}-07-14`, 'Fête Nationale');
    holidays.set(`${year}-08-15`, 'Assomption');
    holidays.set(`${year}-11-01`, 'Toussaint');
    holidays.set(`${year}-11-11`, 'Armistice 1918');
    holidays.set(`${year}-12-25`, 'Noël');

    // Jours basés sur Pâques
    const easter = getEasterDate(year);

    // Lundi de Pâques (Pâques + 1)
    const easterMonday = new Date(easter);
    easterMonday.setDate(easter.getDate() + 1);
    holidays.set(format(easterMonday, 'yyyy-MM-dd'), 'Lundi de Pâques');

    // Ascension (Pâques + 39)
    const ascension = new Date(easter);
    ascension.setDate(easter.getDate() + 39);
    holidays.set(format(ascension, 'yyyy-MM-dd'), 'Ascension');

    // Lundi de Pentecôte (Pâques + 50)
    const pentecostMonday = new Date(easter);
    pentecostMonday.setDate(easter.getDate() + 50);
    holidays.set(format(pentecostMonday, 'yyyy-MM-dd'), 'Lundi de Pentecôte');

    return holidays;
}

// Check si une date est un jour férié
function isHoliday(date: Date): { isHoliday: boolean; name?: string } {
    const year = getYear(date);
    const holidays = getFrenchHolidays(year);
    const dateStr = format(date, 'yyyy-MM-dd');
    const holidayName = holidays.get(dateStr);
    return { isHoliday: !!holidayName, name: holidayName };
}

export function WeekView() {
    const navigate = useNavigate();
    const api = useApi();
    const queryClient = useQueryClient();
    const { date: dateParam } = useParams();
    const [selectedWeek, setSelectedWeek] = useState(() => dateDepuisUrl(dateParam));
    const { isAuthenticated, isLoading: authLoading } = useAuth();

    const weekStart = startOfWeek(selectedWeek, { weekStartsOn: 1 });
    const weekEnd = endOfWeek(selectedWeek, { weekStartsOn: 1 });
    const weekDays = eachDayOfInterval({ start: weekStart, end: weekEnd });

    // Fetch week entries - only when authenticated
    const { data: entriesData, isLoading } = useQuery({
        queryKey: ['time-entries', 'week', format(weekStart, 'yyyy-MM-dd')],
        queryFn: () =>
            api.get<ApiResponse<{ entries: TimeEntryType[]; weekStart: string; weekEnd: string; dailyTotals: Record<string, number>; weekTotal: number }>>(`/api/time-entries/week/${format(weekStart, 'yyyy-MM-dd')}`, {
                myEntries: true,
            }),
        enabled: isAuthenticated && !authLoading,
    });

    const entries = entriesData?.data?.entries || [];

    // Soumet les brouillons de la semaine affichée, comme la vue mois. L'ancien appel visait
    // /batch/submit, qu'aucune API n'a jamais servi : la route /:id/submit le prenait pour
    // un identifiant et répondait 500, sans rien soumettre.
    const submitAllMutation = useMutation({
        mutationFn: () => {
            const draftIds = entries.filter((e) => e.status === 'draft').map((e) => e.id);
            return api.post('/api/time-entries/submit-batch', { entryIds: draftIds });
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['time-entries'] });
        },
    });

    const goToPreviousWeek = () => setSelectedWeek(subWeeks(selectedWeek, 1));
    const goToNextWeek = () => setSelectedWeek(addWeeks(selectedWeek, 1));
    const goToToday = () => setSelectedWeek(new Date());

    // Group entries by date
    const entriesByDate = weekDays.map((day) => {
        const dayStr = format(day, 'yyyy-MM-dd');
        const dayEntries = entries.filter(
            (e) => format(new Date(e.entryDate), 'yyyy-MM-dd') === dayStr
        );
        const totalHours = dayEntries.reduce((sum, e) => sum + e.hours, 0);
        const hasDrafts = dayEntries.some((e) => e.status === 'draft');
        const hasRejected = dayEntries.some((e) => e.status === 'rejected');
        return {
            date: day,
            dateStr: dayStr,
            entries: dayEntries,
            totalHours,
            hasDrafts,
            hasRejected,
        };
    });

    const weekTotalDays = hoursToDays(entriesByDate.reduce((sum, d) => sum + d.totalHours, 0));
    const hasDraftsInWeek = entriesByDate.some((d) => d.hasDrafts);

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                    <button
                        onClick={goToPreviousWeek}
                        className="p-2 hover:bg-[var(--color-bg)] rounded-lg transition-colors"
                    >
                        <ChevronLeft className="w-5 h-5" />
                    </button>

                    <div className="text-center">
                        <h1 className="text-xl font-bold text-[var(--color-text)]">
                            Semaine du {format(weekStart, 'd', { locale: fr })} au{' '}
                            {format(weekEnd, 'd MMMM yyyy', { locale: fr })}
                        </h1>
                    </div>

                    <button
                        onClick={goToNextWeek}
                        className="p-2 hover:bg-[var(--color-bg)] rounded-lg transition-colors"
                    >
                        <ChevronRight className="w-5 h-5" />
                    </button>
                </div>

                <div className="flex items-center gap-2">
                    <button
                        onClick={goToToday}
                        className="flex items-center gap-2 px-3 py-2 text-sm text-[var(--color-text-secondary)] hover:bg-[var(--color-bg)] rounded-lg transition-colors"
                    >
                        <Calendar className="w-4 h-4" />
                        Aujourd'hui
                    </button>

                    {hasDraftsInWeek && (
                        <button
                            onClick={() => submitAllMutation.mutate()}
                            disabled={submitAllMutation.isPending}
                            className="btn-primary"
                        >
                            {submitAllMutation.isPending ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                                <Send className="w-4 h-4" />
                            )}
                            Soumettre la semaine
                        </button>
                    )}
                </div>
            </div>

            {submitAllMutation.isError && (
                <p role="alert" className="text-sm text-[var(--color-error)]">
                    La semaine n'a pas pu être soumise : {submitAllMutation.error.message}
                </p>
            )}

            {/* Week Summary */}
            <div className="card">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <Clock className="w-5 h-5 text-[var(--color-text-muted)]" />
                        <span className="text-[var(--color-text-secondary)]">Total de la semaine</span>
                    </div>
                    <div className="text-right">
                        <span
                            className={cn(
                                'text-2xl font-bold',
                                weekTotalDays >= 5
                                    ? 'text-[var(--color-success)]'
                                    : weekTotalDays >= 4
                                        ? 'text-[var(--color-warning)]'
                                        : 'text-[var(--color-text)]'
                            )}
                        >
                            {weekTotalDays}j
                        </span>
                        <span className="text-[var(--color-text-muted)] text-sm ml-2">/ 5j</span>
                    </div>
                </div>
                {weekTotalDays < 5 && weekTotalDays > 0 && (
                    <div className="mt-3">
                        <div className="h-2 bg-[var(--color-bg)] rounded-full overflow-hidden">
                            <div
                                className="h-full bg-[var(--color-primary)] rounded-full transition-all duration-300"
                                style={{ width: `${Math.min((weekTotalDays / 5) * 100, 100)}%` }}
                            />
                        </div>
                        <p className="text-sm text-[var(--color-text-secondary)] mt-1">
                            Il manque {Math.round((5 - weekTotalDays) * 100) / 100}j pour atteindre la semaine standard
                        </p>
                    </div>
                )}
            </div>

            {/* Week Grid */}
            {isLoading ? (
                <div className="flex items-center justify-center py-12">
                    <Loader2 className="w-8 h-8 animate-spin text-[var(--color-primary)]" />
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-7 gap-3">
                    {entriesByDate.map(({ date, dateStr, entries, totalHours, hasDrafts, hasRejected }) => (
                        <DayCard
                            key={dateStr}
                            date={date}
                            entries={entries}
                            totalHours={totalHours}
                            hasDrafts={hasDrafts}
                            hasRejected={hasRejected}
                            onClick={() => navigate(`/time-entry/${dateStr}`)}
                        />
                    ))}
                </div>
            )}

            {/* Legend */}
            <div className="flex flex-wrap items-center gap-4 text-sm">
                <div className="flex items-center gap-2">
                    <span className="w-3 h-3 rounded-full bg-[var(--color-bg-hover)]" />
                    <span className="text-[var(--color-text-secondary)]">Brouillon</span>
                </div>
                <div className="flex items-center gap-2">
                    <span className="w-3 h-3 rounded-full bg-[var(--color-warning)]" />
                    <span className="text-[var(--color-text-secondary)]">Soumis</span>
                </div>
                <div className="flex items-center gap-2">
                    <span className="w-3 h-3 rounded-full bg-[var(--color-success)]" />
                    <span className="text-[var(--color-text-secondary)]">Validé</span>
                </div>
                <div className="flex items-center gap-2">
                    <span className="w-3 h-3 rounded-full bg-[var(--color-error)]" />
                    <span className="text-[var(--color-text-secondary)]">Rejeté</span>
                </div>
                <div className="flex items-center gap-2">
                    <span className="w-3 h-3 rounded-full bg-[var(--color-primary)]" />
                    <span className="text-[var(--color-text-secondary)]">Facturé</span>
                </div>
                <div className="flex items-center gap-2 ml-4 pl-4 border-l border-[var(--color-border)]">
                    <AlertTriangle className="w-3 h-3 text-orange-500" />
                    <span className="text-[var(--color-text-secondary)]">Week-end/Férié ou &gt;1j</span>
                </div>
            </div>
        </div>
    );
}

interface DayCardProps {
    date: Date;
    entries: TimeEntryType[];
    totalHours: number;
    hasDrafts: boolean;
    hasRejected: boolean;
    onClick: () => void;
}

function DayCard({
    date,
    entries,
    totalHours,
    hasDrafts,
    hasRejected,
    onClick,
}: DayCardProps) {
    const isCurrentDay = isToday(date);
    const isWeekend = date.getDay() === 0 || date.getDay() === 6;
    const totalDays = hoursToDays(totalHours);
    const holidayInfo = isHoliday(date);
    const isSpecialDay = isWeekend || holidayInfo.isHoliday;

    // Alerte si saisie sur jour spécial OU si plus de 1 jour
    const hasWarning = (isSpecialDay && entries.length > 0) || totalDays > 1;

    return (
        <button
            onClick={onClick}
            className={cn(
                'card text-left transition-all hover:shadow-md relative',
                isCurrentDay && 'border-[var(--color-primary)] ring-2 ring-[var(--color-primary-light)]',
                isSpecialDay && 'bg-[var(--color-bg)]',
                hasWarning && 'border-orange-400 ring-1 ring-orange-200'
            )}
        >
            {/* Warning indicator */}
            {hasWarning && (
                <div className="absolute -top-1.5 -right-1.5">
                    <AlertTriangle className="w-4 h-4 text-orange-500" />
                </div>
            )}

            {/* Day header */}
            <div className="flex items-center justify-between mb-3">
                <div>
                    <p className={cn(
                        'text-xs uppercase',
                        isSpecialDay ? 'text-orange-500 font-medium' : 'text-[var(--color-text-muted)]'
                    )}>
                        {format(date, 'EEE', { locale: fr })}
                    </p>
                    <p
                        className={cn(
                            'text-lg font-bold',
                            isCurrentDay ? 'text-[var(--color-primary)]' : 'text-[var(--color-text)]'
                        )}
                    >
                        {format(date, 'd')}
                    </p>
                </div>
                <span
                    className={cn(
                        'text-lg font-semibold',
                        totalDays > 1
                            ? 'text-orange-500'
                            : totalDays >= 1
                                ? 'text-[var(--color-success)]'
                                : totalDays > 0
                                    ? 'text-[var(--color-warning)]'
                                    : 'text-[var(--color-text-muted)]'
                    )}
                >
                    {totalDays}j
                </span>
            </div>

            {/* Entries preview */}
            {entries.length > 0 ? (
                <div className="space-y-1">
                    {entries.slice(0, 3).map((entry) => (
                        <div
                            key={entry.id}
                            className="flex items-center gap-2 text-xs"
                        >
                            <span
                                className={cn(
                                    'w-2 h-2 rounded-full flex-shrink-0',
                                    entry.status === 'draft' && 'bg-[var(--color-bg-hover)]',
                                    entry.status === 'submitted' && 'bg-[var(--color-warning)]',
                                    entry.status === 'approved' && 'bg-[var(--color-success)]',
                                    entry.status === 'rejected' && 'bg-[var(--color-error)]',
                                    entry.status === 'billed' && 'bg-[var(--color-primary)]'
                                )}
                            />
                            <span className="text-[var(--color-text-secondary)] truncate flex-1">
                                {entry.projectCode}
                            </span>
                            <span className="text-[var(--color-text-muted)]">{hoursToDays(entry.hours)}j</span>
                        </div>
                    ))}
                    {entries.length > 3 && (
                        <p className="text-xs text-[var(--color-text-muted)]">
                            +{entries.length - 3} autre{entries.length > 4 ? 's' : ''}
                        </p>
                    )}
                </div>
            ) : (
                <p className={cn(
                    'text-xs italic',
                    isSpecialDay ? 'text-orange-500' : 'text-[var(--color-text-muted)]'
                )}>
                    {holidayInfo.isHoliday ? holidayInfo.name : isWeekend ? 'Week-end' : 'Aucune saisie'}
                </p>
            )}

            {/* Alerts */}
            {(hasDrafts || hasRejected) && (
                <div className="mt-2 pt-2 border-t border-[var(--color-border)]">
                    {hasDrafts && (
                        <span className="text-xs text-[var(--color-text-secondary)]">À soumettre</span>
                    )}
                    {hasRejected && (
                        <span className="text-xs text-[var(--color-error)] ml-2">À corriger</span>
                    )}
                </div>
            )}
        </button>
    );
}
